import { defineStore } from 'pinia';
import { platformAPI } from '../api/platform';

export const useMemberStore = defineStore('member', {
  state: () => ({
    currentMember: null,
    apiKeys: [],
    cloudflareCredential: null,
    isAuthenticated: false,
    hydrated: false,
    lastCreatedApiKey: null,
    loading: false,
  }),

  getters: {
    canUseApi: (state) => Boolean(state.currentMember?.canUseApi),
    hasSavedCloudflareToken: (state) => Boolean(state.cloudflareCredential?.tokenHint),
  },

  actions: {
    async hydrate() {
      if (this.hydrated) {
        return;
      }

      await this.refreshSession();
      this.hydrated = true;
    },

    async refreshSession() {
      this.loading = true;
      try {
        const data = await platformAPI.getCurrentMember();
        this.currentMember = data.member;
        this.apiKeys = data.apiKeys || [];
        this.cloudflareCredential = data.cloudflareCredential || null;
        this.isAuthenticated = true;
      } catch (error) {
        this.currentMember = null;
        this.apiKeys = [];
        this.cloudflareCredential = null;
        this.isAuthenticated = false;
      } finally {
        this.loading = false;
      }
    },

    async register(payload) {
      const data = await platformAPI.register(payload);
      this.currentMember = data.member;
      this.apiKeys = data.apiKeys || [];
      this.cloudflareCredential = data.cloudflareCredential || null;
      this.isAuthenticated = true;
      this.lastCreatedApiKey = null;
      return data.member;
    },

    async login(email, password) {
      const data = await platformAPI.login({ email, password });
      this.currentMember = data.member;
      this.apiKeys = data.apiKeys || [];
      this.cloudflareCredential = data.cloudflareCredential || null;
      this.isAuthenticated = true;
      this.lastCreatedApiKey = null;
      return data.member;
    },

    async logout() {
      await platformAPI.logout();
      this.currentMember = null;
      this.apiKeys = [];
      this.cloudflareCredential = null;
      this.isAuthenticated = false;
      this.lastCreatedApiKey = null;
    },

    async createApiKey(name = 'Primary Integration Key') {
      const data = await platformAPI.createApiKey(name);
      this.apiKeys = [data.apiKey, ...this.apiKeys];
      this.lastCreatedApiKey = data.plainTextKey;
      return data;
    },

    async revokeApiKey(keyId) {
      await platformAPI.revokeApiKey(keyId);
      this.apiKeys = this.apiKeys.filter((key) => key.id !== keyId);
    },

    async saveCloudflareCredential(token, label) {
      const data = await platformAPI.saveCloudflareCredential({ token, label });
      this.currentMember = data.member;
      this.cloudflareCredential = data.cloudflareCredential || null;
      return data.cloudflareCredential;
    },

    async deleteCloudflareCredential() {
      const data = await platformAPI.deleteCloudflareCredential();
      this.currentMember = data.member;
      this.cloudflareCredential = null;
      return data;
    },

    async createSuperVip(payload) {
      return platformAPI.createSuperVip(payload);
    },

    async resetMemberPassword(payload) {
      return platformAPI.resetMemberPassword(payload);
    },

    async bootstrapSuperAdminPassword(payload) {
      const data = await platformAPI.bootstrapSuperAdminPassword(payload);
      this.currentMember = data.member;
      this.apiKeys = data.apiKeys || [];
      this.cloudflareCredential = data.cloudflareCredential || null;
      this.isAuthenticated = true;
      this.lastCreatedApiKey = null;
      return data;
    },
  },
});
